import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { settingsApiClient } from "@api/settings-api-client";
import { pruneOrphanedColumns, getVisibleOrderedColumns, mergeColumnPreferences, normalizeColumnOrders } from "@context-providers/table/column-preferences-utils";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
const ColumnPreferencesContext = createContext(undefined);

/** Props for the ColumnPreferencesProvider component */

/**
 * Custom provider to manage column visibility state with persistence via user settings API.
 *
 * Features:
 * - Fetches table column preferences from user settings on mount
 * - Updates preferences when visibility changes
 * - Ensures "options" column is always visible
 */
export function ColumnPreferencesProvider({
  tableKey,
  columns,
  options,
  children
}) {
  const [settingsData, setSettingsData] = useState(null);
  const [currentColumnPreferences, setCurrentColumnPreferences] = useState(undefined);
  const {
    handleErrorWithToast
  } = useErrorContext();

  // Columns that must always stay visible and be placed after data columns (e.g. options)
  const alwaysVisibleColumns = options?.nonConfigurableColumns ?? [];
  const alwaysVisibleSet = new Set(alwaysVisibleColumns);

  // Split between data columns and the non-configurable ones; build a unified set of valid columns
  const dataColumns = columns.filter(col => !alwaysVisibleSet.has(col));
  const validColumnsSet = new Set([...dataColumns, ...alwaysVisibleColumns]);

  // The merge inputs are read from a ref, so that both the fetch, which resolves at an arbitrary
  // point in time, and the reconciliation below always use the columns of the latest render
  const mergeInputsRef = useRef({
    dataColumns,
    alwaysVisibleColumns,
    validColumnsSet,
    defaultVisibleColumns: options?.defaultVisibleColumns
  });

  // The preferences of this table as they are stored in the user settings, including the columns
  // that the table does not know about yet
  const storedTablePreferencesRef = useRef({});
  useEffect(() => {
    mergeInputsRef.current = {
      dataColumns,
      alwaysVisibleColumns,
      validColumnsSet,
      defaultVisibleColumns: options?.defaultVisibleColumns
    };
  });

  /** Merges the given preferences with the columns that the table currently has */
  function mergeCurrentColumnPreferences(preferences) {
    const inputs = mergeInputsRef.current;
    return mergeColumnPreferences(preferences, inputs.dataColumns, inputs.alwaysVisibleColumns, inputs.validColumnsSet, inputs.defaultVisibleColumns);
  }

  /**
   * Reconcile the preferences whenever the set of columns changes.
   *
   * Some columns are only known after the first render, e.g. the SCENE column of the workspace
   * members table appears once the workspace subscriptions are fetched. Without this the column
   * would have no entry in the preferences and would therefore never be displayed.
   */
  const columnsKey = columns.join("|");
  useEffect(() => {
    setCurrentColumnPreferences(preferences => {
      if (!preferences) {
        return preferences;
      }

      // The stored preferences of a column that was unknown until now were pruned from the current
      // ones, so they are taken into account again to not lose the choice of the user
      return mergeCurrentColumnPreferences({
        ...storedTablePreferencesRef.current,
        ...preferences
      });
    });
    // Only a change of the columns themselves should trigger the reconciliation
  }, [columnsKey]);

  /**
   * Fetch table column preferences from user settings API on mount.
   * If no preferences exist, create default with all columns visible.
   */
  useEffect(() => {
    async function fetchPreferences() {
      try {
        const {
          value,
          modifiedAt
        } = await settingsApiClient.user.fetchUserSettings("table_column_preferences");
        setSettingsData({
          value,
          modifiedAt
        });
        const tablePreferences = value[tableKey] ?? {};
        storedTablePreferencesRef.current = tablePreferences;

        // The columns may have changed while the settings were being fetched, so the merge is
        // always done against the columns of the latest render
        setCurrentColumnPreferences(mergeCurrentColumnPreferences(tablePreferences));
      } catch (error) {
        handleErrorWithToast({
          id: `fetch-table-column-preferences-${Date.now().toString()}`,
          title: "Failed to load your column preferences. Using default settings.",
          error
        });
      }
    }
    void fetchPreferences();
    // Only tableKey should trigger this effect to prevent unnecessary re-fetches
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableKey]);

  /**
   * Persists column preferences to user settings API.
   * Ensures "options" is always visible and merges with other tables' preferences.
   */
  async function updateColumnPreferences(newColumnPreferences) {
    try {
      if (!settingsData) {
        handleErrorWithToast({
          id: `settings-data-table-column-preferences-${Date.now().toString()}`,
          title: "Unable to save column preferences. Please try again.",
          error: new Error("Settings data not loaded yet")
        });
        return;
      }
      const fixedPreferences = {
        ...newColumnPreferences
      };

      // Drop prefs for columns that no longer exist
      pruneOrphanedColumns(fixedPreferences, validColumnsSet);
      const normalizedPreferences = normalizeColumnOrders(fixedPreferences, alwaysVisibleColumns);

      // Merge with existing preferences from other tables
      const updatedPreferences = {
        ...settingsData.value,
        [tableKey]: normalizedPreferences
      };
      const {
        value,
        modifiedAt
      } = await settingsApiClient.user.updateUserSettings("table_column_preferences", {
        value: updatedPreferences,
        modifiedAt: settingsData.modifiedAt
      });
      setSettingsData({
        value,
        modifiedAt
      });
      storedTablePreferencesRef.current = {
        ...storedTablePreferencesRef.current,
        ...value[tableKey]
      };
      setCurrentColumnPreferences(value[tableKey]);
    } catch (error) {
      handleErrorWithToast({
        id: `update-table-column-preferences-${Date.now().toString()}`,
        title: "Failed to save column preferences. Your changes were not saved.",
        error
      });
    }
  }

  // Until the stored preferences arrive, the default columns are shown, so that the table does not
  // show other columns while it loads and then jump to the stored ones
  const shownColumnPreferences = currentColumnPreferences ?? mergeColumnPreferences({}, dataColumns, alwaysVisibleColumns, validColumnsSet, options?.defaultVisibleColumns);
  const visibleOrderedColumns = getVisibleOrderedColumns(withTemporarilyVisibleColumns(shownColumnPreferences, options?.temporarilyVisibleColumns ?? []), dataColumns, alwaysVisibleColumns, validColumnsSet, alwaysVisibleSet);
  const value = {
    updateColumnPreferences,
    updateColumnPreferencesLocally: setCurrentColumnPreferences,
    columnPreferences: currentColumnPreferences,
    visibleOrderedColumns,
    dataColumns
  };
  return <ColumnPreferencesContext.Provider value={value}>
      {children}
    </ColumnPreferencesContext.Provider>;
}

/** Returns the preferences with the given columns made visible, leaving the preferences untouched */
function withTemporarilyVisibleColumns(preferences, columns) {
  if (!preferences || !columns.length) {
    return preferences;
  }
  const updated = {
    ...preferences
  };
  for (const column of columns) {
    const preference = updated[column];
    if (preference) {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- Used in backend
      updated[column] = {
        ...preference,
        visible: true
      };
    }
  }
  return updated;
}
export function useColumnPreferencesContext() {
  const context = useContext(ColumnPreferencesContext);
  if (!context) {
    throw new Error("useColumnPreferencesContext must be used within a ColumnPreferencesProvider");
  }
  return context;
}